import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
    Check, 
    X, 
    Zap, 
    Building2, 
    Crown,
    Loader2,
    Users,
    FolderOpen,
    FileAudio,
    Filter,
    ListChecks,
    Headphones,
    Music,
    ArrowLeft
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const PLAN_ICONS = {
    starter: Zap,
    pro: Building2,
    studio: Crown
};

const PLAN_COLORS = {
    starter: 'from-blue-500 to-blue-600',
    pro: 'from-purple-500 to-purple-600',
    studio: 'from-amber-500 to-amber-600'
};

// Public layout for non-authenticated users
const PublicLayout = ({ children }) => (
    <div className="min-h-screen bg-[#0A0A0A]">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-[#222]">
            <div className="max-w-7xl mx-auto px-6 py-4">
                <div className="flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F51E1E] to-[#FF6B6B] flex items-center justify-center">
                            <Music className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-xl font-bold text-white">Pitch Room</span>
                    </Link>

                    <div className="flex items-center gap-6">
                        <Link to="/" className="text-[#A3A3A3] hover:text-white transition-colors flex items-center gap-1">
                            <ArrowLeft className="w-4 h-4" />
                            Back to Home
                        </Link>
                        <Link to="/poster-signup" className="text-[#A3A3A3] hover:text-white transition-colors">
                            For Music Buyers
                        </Link>
                        <Link to="/login">
                            <Button variant="outline" className="border-[#333] text-white hover:bg-[#1A1A1A]">
                                Login
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        </nav>

        {/* Content */}
        <main className="pt-24 pb-12">
            {children}
        </main>

        {/* Footer */}
        <footer className="border-t border-[#222] py-8">
            <div className="max-w-7xl mx-auto px-6">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#F51E1E] to-[#FF6B6B] flex items-center justify-center">
                            <Music className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-bold text-white">Pitch Room</span>
                    </div>
                    <div className="flex items-center gap-6 text-sm text-[#888]">
                        <Link to="/" className="hover:text-white transition-colors">Home</Link>
                        <Link to="/poster-signup" className="hover:text-white transition-colors">For Music Buyers</Link>
                        <Link to="/login" className="hover:text-white transition-colors">Login</Link>
                    </div>
                    <p className="text-sm text-[#666]">© 2026 Pitch Room</p>
                </div>
            </div>
        </footer>
    </div>
);

export default function PricingPage() {
    const { token, user } = useAuth();
    const navigate = useNavigate();
    const [plans, setPlans] = useState([]);
    const [trialDays, setTrialDays] = useState(7);
    const [loading, setLoading] = useState(true);
    const [checkoutLoading, setCheckoutLoading] = useState(null);
    const [currentSubscription, setCurrentSubscription] = useState(null);

    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    useEffect(() => {
        fetchPlans();
        if (token && user?.role === 'poster') {
            fetchCurrentSubscription();
        }
    }, [token, user]);

    const fetchPlans = async () => {
        try {
            const response = await fetch(`${API}/subscription/plans`);
            if (response.ok) {
                const data = await response.json();
                setPlans(data.plans);
                setTrialDays(data.trial_days);
            }
        } catch (error) {
            console.error('Error fetching plans:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCurrentSubscription = async () => {
        try {
            const response = await fetch(`${API}/subscription/my-subscription`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setCurrentSubscription(data.subscription);
            }
        } catch (error) {
            console.error('Error fetching subscription:', error);
        }
    };

    const handleSubscribe = async (planKey) => {
        if (!token) {
            navigate('/login', { state: { from: '/pricing', plan: planKey } });
            return;
        }

        if (user?.role !== 'poster') {
            toast.error('Only posters can subscribe to plans');
            return;
        }

        setCheckoutLoading(planKey);
        try {
            const response = await fetch(`${API}/subscription/create-checkout`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    plan: planKey,
                    origin_url: window.location.origin
                })
            });

            if (response.ok) {
                const data = await response.json();
                window.location.href = data.url;
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to start checkout');
            }
        } catch (error) {
            toast.error('Error starting checkout');
        } finally {
            setCheckoutLoading(null);
        }
    };

    const formatFeatureValue = (key, value) => {
        if (value === -1) return 'Unlimited';
        if (typeof value === 'boolean') return value;
        return value;
    };

    const getFeatureLabel = (key) => {
        const labels = {
            active_projects: 'Active Projects',
            submissions_per_project: 'Submissions per Project',
            team_seats: 'Team Seats',
            bulk_actions: 'Bulk Actions',
            advanced_filters: 'Advanced Filters',
            priority_support: 'Priority Support'
        };
        return labels[key] || key;
    };

    const getFeatureIcon = (key) => {
        const icons = {
            active_projects: FolderOpen,
            submissions_per_project: FileAudio,
            team_seats: Users,
            bulk_actions: ListChecks,
            advanced_filters: Filter,
            priority_support: Headphones
        };
        return icons[key] || Check;
    };

    if (loading) {
        // Show simple loading for public view
        if (!token) {
            return (
                <PublicLayout>
                    <div className="flex items-center justify-center min-h-[60vh]">
                        <Loader2 className="w-8 h-8 animate-spin text-[#F51E1E]" />
                    </div>
                </PublicLayout>
            );
        }
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[60vh]">
                    <Loader2 className="w-8 h-8 animate-spin text-[#F51E1E]" />
                </div>
            </Layout>
        );
    }

    // Use public layout for non-authenticated users
    const Wrapper = token ? Layout : PublicLayout;

    return (
        <Wrapper>
            <div className="max-w-6xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="text-center mb-12">
                    <motion.h1 
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-4xl font-bold text-white mb-4"
                    >
                        Choose Your Plan
                    </motion.h1>
                    <motion.p 
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-[#A3A3A3] text-lg max-w-2xl mx-auto"
                    >
                        Start your {trialDays}-day free trial today. No credit card required until trial ends.
                    </motion.p>
                </div>

                {/* Current Plan Badge */}
                {currentSubscription && (
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-center mb-8"
                    >
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30 px-4 py-2">
                            Current Plan: {currentSubscription.plan.charAt(0).toUpperCase() + currentSubscription.plan.slice(1)}
                            {currentSubscription.status === 'trialing' && ' (Trial)'}
                        </Badge>
                    </motion.div>
                )}

                {/* Pricing Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {plans.map((plan, index) => {
                        const Icon = PLAN_ICONS[plan.key] || Zap;
                        const isCurrentPlan = currentSubscription?.plan === plan.key;
                        const isPopular = plan.key === 'pro';

                        return (
                            <motion.div
                                key={plan.key}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className={`
                                    relative bg-[#1A1A1A] border rounded-2xl overflow-hidden
                                    ${isPopular ? 'border-purple-500 ring-2 ring-purple-500/20' : 'border-[#333]'}
                                    ${isCurrentPlan ? 'ring-2 ring-green-500/30' : ''}
                                `}
                            >
                                {isPopular && (
                                    <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-purple-500 to-purple-600 text-white text-center py-1 text-sm font-medium">
                                        Most Popular
                                    </div>
                                )}

                                <div className={`p-6 ${isPopular ? 'pt-10' : ''}`}>
                                    {/* Plan Header */}
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${PLAN_COLORS[plan.key]} flex items-center justify-center`}>
                                            <Icon className="w-6 h-6 text-white" />
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                                            {isCurrentPlan && (
                                                <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                                                    Current Plan
                                                </Badge>
                                            )}
                                        </div>
                                    </div>

                                    {/* Price */}
                                    <div className="mb-6">
                                        <div className="flex items-baseline gap-1">
                                            <span className="text-4xl font-bold text-white">${plan.price}</span>
                                            <span className="text-[#888]">/{plan.interval}</span>
                                        </div>
                                        <p className="text-sm text-[#666] mt-1">
                                            {trialDays}-day free trial included
                                        </p>
                                    </div>

                                    {/* Features */}
                                    <div className="space-y-3 mb-6">
                                        {Object.entries(plan.features).map(([key, value]) => {
                                            const FeatureIcon = getFeatureIcon(key);
                                            const displayValue = formatFeatureValue(key, value);
                                            const isBoolean = typeof value === 'boolean';

                                            return (
                                                <div key={key} className="flex items-center gap-3">
                                                    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                                                        (isBoolean && value) || (!isBoolean && value !== 0)
                                                            ? 'bg-green-500/20 text-green-400'
                                                            : 'bg-[#333] text-[#666]'
                                                    }`}>
                                                        {isBoolean ? (
                                                            value ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />
                                                        ) : (
                                                            <FeatureIcon className="w-3 h-3" />
                                                        )}
                                                    </div>
                                                    <span className="text-sm text-[#A3A3A3]">
                                                        {isBoolean ? (
                                                            getFeatureLabel(key)
                                                        ) : (
                                                            <>
                                                                <span className="text-white font-medium">{displayValue}</span>
                                                                {' '}{getFeatureLabel(key)}
                                                            </>
                                                        )}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* CTA Button */}
                                    <Button
                                        onClick={() => handleSubscribe(plan.key)}
                                        disabled={checkoutLoading === plan.key || isCurrentPlan}
                                        className={`w-full ${
                                            isPopular 
                                                ? 'bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700' 
                                                : 'bg-[#F51E1E] hover:bg-[#D41212]'
                                        } text-white`}
                                        data-testid={`subscribe-${plan.key}-btn`}
                                    >
                                        {checkoutLoading === plan.key ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                Loading...
                                            </>
                                        ) : isCurrentPlan ? (
                                            'Current Plan'
                                        ) : (
                                            `Start Free Trial`
                                        )}
                                    </Button>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                {/* FAQ / Additional Info */}
                <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="mt-12 text-center"
                >
                    <p className="text-[#666] text-sm">
                        Questions? Contact us at{' '}
                        <a href="mailto:support@pitchroom.com" className="text-[#F51E1E] hover:underline">
                            support@pitchroom.com
                        </a>
                    </p>
                </motion.div>
            </div>
        </Wrapper>
    );
}
